import React, { useContext, useEffect, useState } from 'react';

import { AmpPropsContext } from '../../context/amp-props/amp-props-provider';
import { IAmpPropsTag } from '../../typings/amp';
import { ComponentProps } from '../../typings/generic-component';
import { getAmpTag } from '../../utils/amp';
import { IClassNames, makeClassNames } from '../../utils/styles';
import AmpImg from '../amp/amp-img';
import './post-image.scss';

export interface IPostImage extends ComponentProps {
  imgSrc: string;
  imgCredits?: string | null;
  imgCaption?: string | null;
  imgAlt?: string | null;
}

const DEFAULT_RENDITION_WIDTH = 620;

const classNames = (): IClassNames =>
  makeClassNames({
    PostImage: 'post-image-v2',
    PostImageCredits: 'post-image__credits-v2',
    PostImageCaption: 'post-image__caption-v2',
  });

const PostImage: React.FC<IPostImage> = ({ imgSrc, imgCredits = '', imgCaption, imgAlt }) => {
  const cn = classNames();
  const [imageSrc, setImageSrc] = useState(imgSrc);
  const [imageSize, setImageSize] = useState<Record<string, number> | undefined>();
  const { ampProps } = useContext(AmpPropsContext);
  const { isAmp } = useContext(AmpPropsContext);

  const ampImg: IAmpPropsTag | undefined = getAmpTag(ampProps, 'img');

  useEffect(() => {
    if (isAmp) {
      const resizeImage = () => {
        if (img.width > DEFAULT_RENDITION_WIDTH) {
          setImageSrc(`${imgSrc}?im=Resize,width=${DEFAULT_RENDITION_WIDTH}`);
        } else {
          setImageSrc(imgSrc);
        }

        const sizes = {
          width: img.width,
          height: img.height,
        };

        setImageSize({ ...sizes });
      };

      const img = new Image();
      img.onload = resizeImage;
      img.src = imgSrc;

      return () => {
        img.onload = null;
      };
    } else {
      return () => {};
    }
  }, [imgSrc, isAmp]);

  return (
    <div className={cn.PostImage}>
      {ampImg && imageSize ? (
        <>
          <AmpImg
            src={imageSrc}
            alt={imgAlt || undefined}
            layout={ampImg.layout}
            width={imageSize.width}
            height={imageSize.height}
          />
          <p className={cn.PostImageCredits} dangerouslySetInnerHTML={{ __html: imgCredits || '' }} />
          <p className={cn.PostImageCaption}>{imgCaption}</p>
        </>
      ) : (
        <figure>
          <img src={imageSrc} alt={imgAlt || undefined} title={imgCaption || undefined} loading="lazy" />
          {(imgCredits || imgCaption) && (
            <figcaption>
              <p className={cn.PostImageCredits} dangerouslySetInnerHTML={{ __html: imgCredits || '' }} />
              <p className={cn.PostImageCaption}>{imgCaption}</p>
            </figcaption>
          )}
        </figure>
      )}
    </div>
  );
};

export default PostImage;
